// 벌점 현황 페이지

import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PersonIcon from '@mui/icons-material/Person';
import { useSupabaseParentAuth } from '../hooks/useSupabaseParentAuth';
import { getAccessToken } from '../../../lib/authUtils';

// 벌점 기록 타입
interface PenaltyRecord {
  id: string;
  penalty_type: string;
  penalty_type_name: string;
  points: number;
  reason: string | null;
  is_auto: boolean;
  created_at: string;
}

// 자녀별 벌점 요약 타입
interface ChildPenaltySummary {
  student_id: string;
  student_name: string;
  total_points: number;
  monthly_points: number;
  record_count: number;
}

// 자녀별 상세 벌점 정보
interface ChildPenaltyDetail {
  student_id: string;
  student_name: string;
  total_points: number;
  monthly_points: number;
  records: PenaltyRecord[];
}

export default function PenaltyPage() {
  const { parent } = useSupabaseParentAuth();

  const [summaries, setSummaries] = useState<ChildPenaltySummary[]>([]);
  const [details, setDetails] = useState<Record<string, ChildPenaltyDetail>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedChild, setExpandedChild] = useState<string | false>(false);

  useEffect(() => {
    if (parent?.id) {
      loadPenaltySummaries();
    }
  }, [parent?.id]);

  const loadPenaltySummaries = async () => {
    if (!parent?.id) return;

    setIsLoading(true);
    setError('');

    try {
      const accessToken = await getAccessToken();
      

      if (!accessToken) {
        setError('로그인이 필요합니다.');
        return;
      }

      const response = await fetch('/api/v1/parent/children/penalties/summary', {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('벌점 정보 조회 실패');
      }

      const data = await response.json();
      setSummaries(data.children || []);

      // 첫 번째 자녀가 있으면 자동 확장
      if (data.children && data.children.length > 0) {
        setExpandedChild(data.children[0].student_id);
        loadChildPenaltyDetails(data.children[0].student_id, accessToken);
      }
    } catch (err: any) {
      console.error('벌점 조회 오류:', err);
      setError(err.message || '벌점 정보를 불러오는데 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const loadChildPenaltyDetails = async (studentId: string, accessTokenParam?: string) => {
    const accessToken = accessTokenParam || await getAccessToken();

    if (!accessToken) return;

    try {
      const response = await fetch(`/api/v1/parent/children/${studentId}/penalties?limit=20`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('벌점 상세 조회 실패');
      }

      const data = await response.json();
      setDetails(prev => ({
        ...prev,
        [studentId]: data,
      }));
    } catch (err) {
      console.error('벌점 상세 조회 오류:', err);
    }
  };

  const handleAccordionChange = (studentId: string) => async (
    _event: React.SyntheticEvent,
    isExpanded: boolean
  ) => {
    setExpandedChild(isExpanded ? studentId : false);

    if (isExpanded && !details[studentId]) {
      loadChildPenaltyDetails(studentId);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getPointsColor = (points: number) => {
    if (points >= 10) return 'error';
    if (points >= 5) return 'warning';
    return 'default';
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        벌점 현황
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        자녀의 벌점 현황을 확인할 수 있습니다.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {summaries.length === 0 ? (
        <Alert severity="info">
          등록된 자녀가 없습니다.
        </Alert>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {summaries.map((summary) => (
            <Accordion
              key={summary.student_id}
              expanded={expandedChild === summary.student_id}
              onChange={handleAccordionChange(summary.student_id)}
              elevation={2}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
                  <PersonIcon color="primary" />
                  <Typography fontWeight={600} sx={{ flexGrow: 1 }}>
                    {summary.student_name}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Chip
                      icon={<WarningAmberIcon />}
                      label={`총 ${summary.total_points}점`}
                      color={getPointsColor(summary.total_points)}
                      size="small"
                    />
                    <Chip
                      label={`이번 달 ${summary.monthly_points}점`}
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Divider sx={{ mb: 2 }} />

                {details[summary.student_id] ? (
                  details[summary.student_id].records.length > 0 ? (
                    <List dense>
                      {details[summary.student_id].records.map((record) => (
                        <ListItem
                          key={record.id}
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            mb: 1,
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mb: 0.5 }}>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {record.penalty_type_name}
                            </Typography>
                            <Chip
                              label={`${record.points}점`}
                              color={record.points >= 3 ? 'error' : 'warning'}
                              size="small"
                            />
                          </Box>
                          {record.reason && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              {record.reason}
                            </Typography>
                          )}
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(record.created_at)}
                            </Typography>
                            {record.is_auto && (
                              <Chip label="자동" size="small" variant="outlined" sx={{ height: 20 }} />
                            )}
                          </Box>
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                      벌점 기록이 없습니다.
                    </Typography>
                  )
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}
    </Box>
  );
}
